import { useCallback } from "react"

import { EditableTitle } from "@coval/ui"

import { isSameResourceName } from "@/utils/validation/resourceName"

import {
  DASHBOARD_NAME_MAX_LENGTH,
  dashboardNameValidationIssue,
} from "../utils/dashboardName"

interface DashboardBreadcrumbTitleProps {
  displayName: string
  /** Names of the organization's other dashboards; one of them cannot be reused. */
  otherNames: readonly string[]
  /** Saves the new name, rejecting on failure so the field stays open with the message. */
  onRename: (name: string) => Promise<void>
}

/** The open dashboard's name as the last breadcrumb, renamed in place and saved on Enter or blur. */
export const DashboardBreadcrumbTitle = ({
  displayName,
  otherNames,
  onRename,
}: DashboardBreadcrumbTitleProps) => {
  const validate = useCallback(
    (name: string) =>
      dashboardNameValidationIssue(name) ??
      (otherNames.some((other) => isSameResourceName(other, name))
        ? "Another dashboard already uses this name."
        : undefined),
    [otherNames],
  )

  return (
    <div className="min-w-0" data-testid="dashboard-name-title">
      <EditableTitle
        size="sm"
        label="Dashboard name"
        placeholder="Untitled"
        value={displayName}
        maxLength={DASHBOARD_NAME_MAX_LENGTH}
        validate={validate}
        onSave={onRename}
      />
    </div>
  )
}
